const ROOTS = {
  AUTH: '/auth',
  DASHBOARD: '/',
  ADMIN: '/admin',
  ORDER_TYPES: '/order-types',
  PRODUCTS: '/products',
  PRODUCER_ORDERS: '/producer_order',
  PUBLIC: '/public',
  COFFEE: '/coffee',
};

// One definition for the profitability detail pages: the nav matches the root, the pages
// build on it (NAT-2556).
const PROFITABILITY_DETAILS = `${ROOTS.ADMIN}/profitability_details`;

// ----------------------------------------------------------------------

export const paths = {
  root: '/',
  page403: '/403',
  page404: '/404',
  cookies: '#',
  login: '/login',
  forgotPassword: '/forgot-password',
  resetPassword: '/reset-password',
  // AUTH
  auth: {
    root: ROOTS.AUTH,
    verify: `${ROOTS.AUTH}/verify`,
    login: `${ROOTS.AUTH}/login`,
  },
  // CUSTOMER
  customer: {
    catalog: `/catalog`,
    checkout: `/checkout`,
    paymentStatus: `/payment-status`,
    orders: { root: `/orders`, details: (order_code: string) => `/orders/${order_code}` },
    producers: '/producers',
  },
  public: {
    downloadProducts: (token: string) => `/downloads/clientPdf/${token}`,
    generic: '/downloads/listino-aggiornato.pdf',
    productPdfPreview: `${ROOTS.PUBLIC}/product-pdf-preview`,
    listinoPreview: '/pdf/listino-aggiornato',
    exciseReport: '/excise-report',
  },
  // ADMIN
  coffee: {
    root: ROOTS.COFFEE,
    dashboard: ROOTS.COFFEE,
  },
  admin: {
    root: ROOTS.ADMIN,
    orderTypes: {
      producerOrders: {
        root: `${ROOTS.ADMIN}/producer-orders`,
        new: `${ROOTS.ADMIN}/producer-orders/new`,
        edit: (order_code: string) => `${ROOTS.ADMIN}/producer-orders/${order_code}/edit`,
        details: (order_code: string) => `${ROOTS.ADMIN}/producer-orders/${order_code}`,
      },
      clientOrders: {
        root: `${ROOTS.ADMIN}/client-orders`,
        new: `${ROOTS.ADMIN}/client-orders/new`,
        edit: (order_code: string) => `${ROOTS.ADMIN}/client-orders/${order_code}/edit`,
        details: (order_code: string) => `${ROOTS.ADMIN}/client-orders/${order_code}`,
      },
    },
    dashboard: {
      operations: `${ROOTS.ADMIN}/dashboard/operations`,
      // NAT-2545 — «Situazione attuale», the whole business on one page. Admin-only.
      situazione: `${ROOTS.ADMIN}/dashboard/situazione`,
      // NAT-2610 — «Da incassare», the first Situazione detail page. Admins and (NAT-2795) agents.
      daIncassare: `${ROOTS.ADMIN}/dashboard/da-incassare`,
      // NAT-2612 — «Magazzino fermo», stock at cost. Admin-only.
      magazzinoFermo: `${ROOTS.ADMIN}/dashboard/magazzino-fermo`,
      overview: `${ROOTS.ADMIN}/dashboard/overview`,
      overlays: {
        root: `${ROOTS.ADMIN}/overlay`,
        new: `${ROOTS.ADMIN}/overlay/new`,
        edit: (overlayId: string) => `${ROOTS.ADMIN}/overlay/${overlayId}/edit`,
        details: (overlayId: string) => `${ROOTS.ADMIN}/overlay/${overlayId}`,
      },
      table_text: `${ROOTS.ADMIN}/dashboard/table-text`,
      assistant: `${ROOTS.ADMIN}/dashboard/assistant`,
    },
    // NAT-2749 — d26 «Portafoglio clienti». Its own URL, not under /admin/clients (a client slug).
    clientPortfolio: `${ROOTS.ADMIN}/client-portfolio`,
    // NAT-2760 — d15 «Analisi produttori» and d17 «Analisi prodotti»: own URLs, as above.
    producerAnalysis: `${ROOTS.ADMIN}/producer-analysis`,
    productAnalysis: `${ROOTS.ADMIN}/product-analysis`,
    // NAT-2781 — d31 «Analisi clienti»: a customer picker over the same engine.
    clientAnalysis: `${ROOTS.ADMIN}/client-analysis`,
    // NAT-2864 — «Qualità stock»: the wines and the producers. Admin only: cost and realisable value.
    stockQuality: {
      vini: `${ROOTS.ADMIN}/stock-quality/vini`,
      produttori: `${ROOTS.ADMIN}/stock-quality/produttori`,
    },
    clients: {
      root: `${ROOTS.ADMIN}/clients`,
      new: `${ROOTS.ADMIN}/clients/new`,
      // NAT-2523 — static, so React Router ranks it above `/clients/:slug`.
      retention: `${ROOTS.ADMIN}/clients/retention`,
      leads: `${ROOTS.ADMIN}/clients/leads`,
      // NAT-2559 — static, so React Router ranks it above `/clients/:slug`.
      scouting: `${ROOTS.ADMIN}/clients/scouting`,
      edit: (slug: string) => `${ROOTS.ADMIN}/clients/${slug}/edit`,
      details: (slug: string) => `${ROOTS.ADMIN}/clients/${slug}`,
    },
    companies: {
      root: `${ROOTS.ADMIN}/companies`,
      details: (companyId: string) => `${ROOTS.ADMIN}/companies/${companyId}`,
    },
    producerCompanies: {
      root: `${ROOTS.ADMIN}/producer-companies`,
      details: (ficEntityId: string) => `${ROOTS.ADMIN}/producer-companies/${ficEntityId}`,
    },
    users: {
      root: `${ROOTS.ADMIN}/users`,
      new: `${ROOTS.ADMIN}/users/new`,
      edit: (slug: string) => `${ROOTS.ADMIN}/users/${slug}/edit`,
      details: (slug: string) => `${ROOTS.ADMIN}/users/${slug}`,
    },
    reservations: {
      root: `${ROOTS.ADMIN}/reservations`,
      new: `${ROOTS.ADMIN}/reservations/new`,
      edit: (client_slug: string, sku: string) =>
        `${ROOTS.ADMIN}/reservations/${client_slug}/product/${sku}/edit`,
    },
    discounts: {
      root: `${ROOTS.ADMIN}/discounts`,
    },
    reconciliation: {
      root: `${ROOTS.ADMIN}/reconciliation`,
    },
    agents: {
      root: `${ROOTS.ADMIN}/agents`,
      new: `${ROOTS.ADMIN}/agents/new`,
      edit: (slug: string) => `${ROOTS.ADMIN}/agents/${slug}/edit`,
      details: (slug: string) => `${ROOTS.ADMIN}/agents/${slug}`,
      // NAT-2749 — d32 «Analisi agenti». A static segment: it ranks above `:slug`.
      analysis: `${ROOTS.ADMIN}/agents/analysis`,
    },
    tastings: {
      root: `${ROOTS.ADMIN}/tastings`,
      new: `${ROOTS.ADMIN}/tastings/new`,
      edit: (slug: string) => `${ROOTS.ADMIN}/tastings/${slug}/edit`,
      details: (slug: string) => `${ROOTS.ADMIN}/tastings/${slug}`,
    },
    products: {
      root: `${ROOTS.ADMIN}${ROOTS.PRODUCTS}`,
      new: `${ROOTS.ADMIN}${ROOTS.PRODUCTS}/new`,
      edit: (sku: string) => `${ROOTS.ADMIN}${ROOTS.PRODUCTS}/${sku}/edit`,
      details: (sku: string) => `${ROOTS.ADMIN}${ROOTS.PRODUCTS}/${sku}`,
    },
    targets: {
      root: `${ROOTS.ADMIN}/targets`,
    },
    controls: {
      root: `${ROOTS.ADMIN}/controls`,
    },
    catalogPdf: {
      root: `${ROOTS.ADMIN}/catalog-pdf`,
    },
    reorderSheet: {
      root: `${ROOTS.ADMIN}/reorder-sheet`,
    },
    // NAT-2654 — «Archivio». A sibling of /products, so it cannot collide with /products/:sku.
    archiveHolds: {
      root: `${ROOTS.ADMIN}/archive-holds`,
    },
    producerFicEntities: {
      root: `${ROOTS.ADMIN}/producer-fic-entities`,
    },
    producers: {
      root: `${ROOTS.ADMIN}/producers`,
      new: `${ROOTS.ADMIN}/producers/new`,
      edit: (slug: string) => `${ROOTS.ADMIN}/producers/${slug}/edit`,
      details: (slug: string) => `${ROOTS.ADMIN}/producers/${slug}`,
    },
    wineTypes: {
      root: `${ROOTS.ADMIN}/wine-types`,
      new: `${ROOTS.ADMIN}/wine-types/new`,
      edit: (slug: string) => `${ROOTS.ADMIN}/wine-types/${slug}/edit`,
      details: (slug: string) => `${ROOTS.ADMIN}/wine-types/${slug}`,
    },
    selections: {
      root: `${ROOTS.ADMIN}/selections`,
      new: `${ROOTS.ADMIN}/selections/new`,
      edit: (id: string) => `${ROOTS.ADMIN}/selections/${id}/edit`,
      details: (id: string) => `${ROOTS.ADMIN}/selections/${id}`,
    },
    wineCategory: {
      root: `${ROOTS.ADMIN}/wine-category`,
    },
    grapeTypes: {
      root: `${ROOTS.ADMIN}/grape-types`,
      new: `${ROOTS.ADMIN}/grape-types/new`,
      edit: (slug: string | null) => `${ROOTS.ADMIN}/grape-types/${slug}/edit`,
      details: (slug: string) => `${ROOTS.ADMIN}/grape-types/${slug}`,
    },
    territory: {
      root: `${ROOTS.ADMIN}/territory`,
      new: `${ROOTS.ADMIN}/territory/new`,
      edit: (slug: string | null) => `${ROOTS.ADMIN}/territory/${slug}/edit`,
      details: (slug: string) => `${ROOTS.ADMIN}/territory/${slug}`,
    },
    statisticTypes: {
      root: `${ROOTS.ADMIN}`,
      profitability: `${ROOTS.ADMIN}/profitability`,
      // Plain string for nav matching — the detail pages sit on a sibling URL,
      // not under /admin/profitability/.
      profitabilityDetailsRoot: PROFITABILITY_DETAILS,
      profitability_details: (slug: string) => `${PROFITABILITY_DETAILS}/${slug}`,
      // NAT-2579 — d22 «Statistiche vendite», on the sales engine. Admin only: margins and cost.
      salesStats: `${ROOTS.ADMIN}/sales-statistics`,
      // NAT-2811 — «Finanza», replacing BI's d36. Admin only, like the BI link it replaces.
      finanza: `${ROOTS.ADMIN}/statistics/finanza`,
    },
    producer_orders: {
      details: (order_code: string) => `${ROOTS.ADMIN}${ROOTS.PRODUCER_ORDERS}/${order_code}`,
    },
  },
  // AGENT
  agent: {
    dashboard: '/agent/dashboard',
    clientDetail: (slug: string) => `/agent/clients/${slug}`,
  },
};
